// Fase 17A-4: zona horaria de reserva para mostrar fechas/horas cuando un registro no tiene una
// tienda identificable (reportes agregados de "todas las tiendas", conexión Loyverse a nivel
// negocio, etc.), en vez de dejar que el navegador use el reloj del dispositivo que las ve (bug
// real: Loyverse mostraba un ticket con una hora distinta a la de la tienda porque usa el reloj
// local del navegador, no el de la tienda). Coincide con el default del backend
// (storeLocalDate.ts, DEFAULT_STORE_TIMEZONE). Para un registro con tienda conocida, usar
// resolveStoreTimezone en su lugar — la app va a operar en varias zonas de México.
export const DEFAULT_STORE_TIMEZONE = 'America/Mexico_City';

export interface TimezoneLookupStore {
  id?: string;
  externalId?: string;
  timezone: string;
}

/**
 * Resuelve la zona horaria real de una tienda a partir de la lista de tiendas ya cargada en la
 * pantalla (evita una llamada extra al backend). Cae a DEFAULT_STORE_TIMEZONE si el registro no
 * trae tienda (reporte agregado de todo el negocio) o si la tienda no aparece en la lista cargada.
 */
export function resolveStoreTimezone(
  stores: TimezoneLookupStore[],
  ref: { storeId?: string | null; storeExternalId?: string | null },
): string {
  if (ref.storeId) {
    const match = stores.find((store) => store.id === ref.storeId);
    if (match) return match.timezone;
  }
  if (ref.storeExternalId) {
    const match = stores.find((store) => store.externalId === ref.storeExternalId);
    if (match) return match.timezone;
  }
  return DEFAULT_STORE_TIMEZONE;
}

// Mismas 12 zonas horarias reales de México que el backend usa para calcular límites de fecha por
// tienda (ver packages/backend/src/infrastructure/utils/storeLocalDate.ts, MEXICO_TIMEZONES —
// verificadas contra los datos de tz del propio runtime de Node, no adivinadas). La mayoría del país
// no observa horario de verano desde la reforma de 2022; los municipios fronterizos sí lo siguen
// para mantenerse sincronizados con las ciudades vecinas de EE. UU.
export const MEXICO_TIMEZONE_OPTIONS: Array<[value: string, label: string]> = [
  ['America/Mexico_City', 'Ciudad de México — Zona Centro (UTC-6)'],
  ['America/Monterrey', 'Monterrey — Zona Centro (UTC-6)'],
  ['America/Merida', 'Mérida — Zona Centro (UTC-6)'],
  ['America/Chihuahua', 'Chihuahua — Zona Centro (UTC-6)'],
  ['America/Bahia_Banderas', 'Bahía de Banderas — Zona Centro (UTC-6)'],
  ['America/Mazatlan', 'Mazatlán — Zona Pacífico (UTC-7)'],
  ['America/Hermosillo', 'Hermosillo — Zona Pacífico (UTC-7, sin horario de verano)'],
  ['America/Cancun', 'Cancún — Zona Sureste (UTC-5)'],
  ['America/Tijuana', 'Tijuana — frontera (UTC-8/UTC-7, con horario de verano)'],
  ['America/Ciudad_Juarez', 'Ciudad Juárez — frontera (UTC-7/UTC-6, con horario de verano)'],
  ['America/Matamoros', 'Matamoros — frontera (UTC-6/UTC-5, con horario de verano)'],
  ['America/Ojinaga', 'Ojinaga — frontera (UTC-6/UTC-5, con horario de verano)'],
];

export function isValidMexicoTimezone(value: string): boolean {
  return MEXICO_TIMEZONE_OPTIONS.some(([zone]) => zone === value);
}

// 'YYYY-MM-DD' del instante dado EN LA ZONA DE LA TIENDA. El "hoy" operativo es el de la tienda,
// no el del dispositivo ni UTC: el dueño puede estar en otra ciudad, y con toISOString() crudo el
// default saltaba al día siguiente después de las ~6 pm (bug real cazado el 25-ago-2026).
export function dateToStoreISO(value: Date = new Date(), timeZone: string = DEFAULT_STORE_TIMEZONE): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(value);
}

// Hora de pared que un instante muestra en la zona dada, expresada como milisegundos UTC del
// "mismo número" — pieza interna de storeWallTimeToIso.
function wallTimeShownInZone(instant: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }).formatToParts(instant);
  const get = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? '0');
  return Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
}

/**
 * Convierte la hora de pared que el usuario tecleó (input datetime-local, 'YYYY-MM-DDTHH:mm') a un
 * instante UTC interpretándola EN LA ZONA DE LA TIENDA — nunca en la del dispositivo. Bug real
 * (26-ago-2026): el rango del corte usaba `new Date(valor)` del navegador, así que el mismo
 * "10:05 AM" producía cortes distintos según la ciudad desde donde se calculara.
 */
export function storeWallTimeToIso(wallTime: string, timeZone: string = DEFAULT_STORE_TIMEZONE): string {
  const normalized = wallTime.length === 16 ? `${wallTime}:00` : wallTime;
  const target = Date.parse(`${normalized}Z`);
  if (Number.isNaN(target)) return new Date(wallTime).toISOString();
  let utcGuess = target;
  // Dos iteraciones: la primera aplica el offset de la zona; la segunda solo corrige si la hora cae
  // exactamente sobre un cambio de horario de verano (zonas fronterizas).
  for (let i = 0; i < 2; i += 1) {
    utcGuess += target - wallTimeShownInZone(new Date(utcGuess), timeZone);
  }
  return new Date(utcGuess).toISOString();
}

/**
 * Hora de pared ('YYYY-MM-DDTHH:mm') que un instante UTC muestra EN LA ZONA DE LA TIENDA — inverso
 * legible de storeWallTimeToIso. Se usa para proponer/mostrar rangos del corte en hora de la tienda
 * sin pasar por el reloj del dispositivo (pre-client-ux-hardening).
 */
export function isoToStoreWallTime(iso: string, timeZone: string = DEFAULT_STORE_TIMEZONE): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).formatToParts(new Date(iso));
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? '00';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}
